import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import notificationServices from 'services/notification-service';
import { RootState } from '../../store';
import {
    NotificationItem,
    NotificationState,
} from './types';

const initialState: NotificationState = {
  notifications: [],
  loading: false,
  error: null,
  activeTab: 'All',
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await notificationServices.getMyNotifications(userId);
      return response?.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (
    {
      notificationId,
      userId,
      ids,
    }: {
      notificationId: number;
      userId: number;
      ids?: number[];
    },
    { rejectWithValue }
  ) => {
    try {
      if (ids && ids.length > 0) {
        // Mark multiple as read
        await Promise.all(
          ids.map((id) =>
            notificationServices.markNotificationAsRead(id, userId)
          )
        );
        return ids;
      } else {
        // Mark single as read
        await notificationServices.markNotificationAsRead(
          notificationId,
          userId
        );
        return [notificationId];
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    addNotification: (state, action: PayloadAction<NotificationItem>) => {
      // Add new notification to the beginning (real-time)
      state.notifications.unshift(action.payload);
    },
    removeNotification: (state, action: PayloadAction<number>) => {
      state.notifications = state.notifications.filter(
        (n) => n.id !== action.payload
      );
    },
    updateNotificationCount: (state, action: PayloadAction<{ id: number; count: number }>) => {
      const notification = state.notifications.find((n) => n.id === action.payload.id);
      if (notification) {
        notification.count = action.payload.count;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNotifications.fulfilled,
        (state, action: PayloadAction<NotificationItem[]>) => {
          state.loading = false;
          // Group similar notifications (same sender, same type)
          const grouped = groupNotifications(action.payload);
          state.notifications = grouped;
        }
      )
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Mark as read
      .addCase(markNotificationAsRead.pending, (state) => {
        state.error = null;
      })
      .addCase(
        markNotificationAsRead.fulfilled,
        (state, action: PayloadAction<number[]>) => {
          const readIds = action.payload;
          state.notifications = state.notifications.map((n) =>
            readIds.includes(n.id) ? { ...n, isRead: true } : n
          );
        }
      )
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// Helper function to group notifications by sender and type
const groupNotifications = (notifications: NotificationItem[]): NotificationItem[] => {
  const grouped: { [key: string]: NotificationItem[] } = {};

  notifications.forEach((notif) => {
    const key = `${notif.type}_${notif.sender?.id}`;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(notif);
  });

  // Convert grouped notifications back to array, aggregating counts
  return Object.values(grouped).map((group) => {
    if (group.length === 1) {
      return group[0];
    }

    // If multiple notifications from same sender/type, merge them
    const first = group[0];
    return {
      ...first,
      ids: group.map((n) => n.id),
      count: group.length,
      message: `${group.length} new ${first.type.toLowerCase()}`,
    };
  });
};

// Selectors
export const selectNotifications = (state: RootState) =>
  state.notificationSlice.notifications;

export const selectNotificationsLoading = (state: RootState) =>
  state.notificationSlice.loading;

export const selectNotificationsError = (state: RootState) =>
  state.notificationSlice.error;

export const selectActiveNotificationTab = (state: RootState) =>
  state.notificationSlice.activeTab;

export const selectUnreadCount = (state: RootState) =>
  state.notificationSlice.notifications.filter((n: { isRead: any; }) => !n.isRead).length;

export const selectGroupedUnreadCount = (state: RootState) => {
  // Count unread groups (not individual notifications)
  return state.notificationSlice.notifications.filter((n: { isRead: any; }) => !n.isRead).length;
};

export const selectFilteredNotifications = (state: RootState) => {
  const { notifications, activeTab } = state.notificationSlice;
  if (activeTab === 'All') {
    return notifications;
  }
  return notifications.filter((n: { type: any; }) => n.type === activeTab);
};

export const selectNotificationsByType = (type: string) => (state: RootState) => {
  return state.notificationSlice.notifications.filter((n: { type: string; }) => n.type === type);
};

export const selectNotificationStats = (state: RootState) => {
  const notifications = state.notificationSlice.notifications;
  return {
    total: notifications.length,
    unread: notifications.filter((n: { isRead: any; }) => !n.isRead).length,
    messages: notifications.filter((n: { type: string; }) => n.type === 'Messages').length,
    bookings: notifications.filter((n: { type: string; }) => n.type === 'Bookings').length,
    referrals: notifications.filter((n: { type: string; }) => n.type === 'Referrals').length,
    bookingRequests: notifications.filter((n: { type: string; }) => n.type === 'Booking Request').length,
  };
};

export const {
  setActiveTab,
  clearNotifications,
  addNotification,
  removeNotification,
  updateNotificationCount,
} = notificationSlice.actions;

export default notificationSlice.reducer;